import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { rupiah } from '@santap/shared';
import type { PaymentInput } from '@santap/shared';
import { api, GalatApi, GalatJaringan } from '../lib/api';

type Tahap = 'pilih' | 'tunai' | 'edc-pilih' | 'edc-proses' | 'qris' | 'selesai';

/**
 * Alur pembayaran.
 *
 * Tunai dan EDC adalah jalur utama, dan keduanya TIDAK butuh internet:
 * tunai ditangani laci, EDC berbicara langsung ke bank lewat ECR di jaringan
 * lokal. QRIS butuh gateway, jadi ia satu-satunya yang mati saat offline —
 * dan itu ditampilkan jujur, bukan disembunyikan.
 */
export function Bayar({
  total, struk, online, tutup, selesai, siapkanPesananDaring, selesaiQris,
}: {
  total: number;
  struk: string;
  online: boolean;
  tutup: () => void;
  selesai: (p: Omit<PaymentInput, 'orderId'>) => Promise<void>;
  /** Kirim pesanan ke server dan kembalikan id-nya. Khusus jalur QRIS. */
  siapkanPesananDaring: () => Promise<string>;
  selesaiQris: () => Promise<void>;
}) {
  const [tahap, setTahap] = useState<Tahap>('pilih');
  const [diterima, setDiterima] = useState(0);
  const [bank, setBank] = useState('');
  const [hasil, setHasil] = useState<any>(null);
  const [galat, setGalat] = useState<string | null>(null);

  const kembalian = diterima - total;

  async function bayarTunai() {
    const p: Omit<PaymentInput, 'orderId'> = {
      id: crypto.randomUUID(), tender: 'tunai', jumlah: total,
      diterima, kembalian,
    };
    setHasil({ tender: 'tunai', kembalian });
    setTahap('selesai');
    await selesai(p);
  }

  async function bayarEdc(namaBank: string) {
    setBank(namaBank);
    setTahap('edc-proses');
    setGalat(null);

    // Di sistem sungguhan, di sini aplikasi berbicara ke jembatan ECR di
    // jaringan lokal: nominal dikirim, mesin menampilkannya, pelanggan tap,
    // lalu mesin membalas approval code. Kasir tidak pernah mengetik ulang —
    // itu penyebab paling umum tutup buku tidak cocok.
    const balasan = await jembatanEcr(namaBank, total);
    if (!balasan.ok) {
      setGalat(balasan.pesan);
      setTahap('edc-pilih');
      return;
    }

    const p: Omit<PaymentInput, 'orderId'> = {
      id: crypto.randomUUID(), tender: 'edc', jumlah: total,
      edc: {
        bank: namaBank,
        approvalCode: balasan.approvalCode,
        kartuAkhir: balasan.kartuAkhir,
        jenisKartu: balasan.jenisKartu,
        batch: balasan.batch,
      },
    };
    setHasil({ tender: 'edc', ...balasan, bank: namaBank });
    setTahap('selesai');
    await selesai(p);
  }

  return (
    <div className="tirai" onClick={(e) => e.target === e.currentTarget && tahap === 'pilih' && tutup()}>
      <div className="modal">
        {tahap === 'pilih' && (
          <>
            <div className="mh">
              <div>
                <h2>Pilih pembayaran</h2>
                <div className="sub">{struk} · {rupiah(total)}</div>
              </div>
              <button className="x" onClick={tutup} aria-label="Tutup">×</button>
            </div>
            <div className="mb">
              <button className="tender pilih" onClick={() => { setDiterima(0); setTahap('tunai'); }}>
                <div className="ic">💵</div>
                <div><b>Tunai</b><span>Hitung kembalian, buka laci</span></div>
              </button>
              <button className="tender" onClick={() => setTahap('edc-pilih')}>
                <div className="ic">💳</div>
                <div><b>Kartu — mesin EDC</b><span>Nominal terkirim otomatis lewat ECR</span></div>
              </button>
              <button className="tender" disabled={!online} onClick={() => setTahap('qris')}>
                <div className="ic">📱</div>
                <div>
                  <b>QRIS</b>
                  <span>{online ? 'Lewat gateway restoran' : 'Tidak tersedia saat offline'}</span>
                </div>
              </button>
              <p className="petunjuk">
                Tunai dan EDC tetap berfungsi penuh tanpa internet — EDC berbicara
                langsung ke bank lewat jaringan lokal. Hanya QRIS yang butuh sambungan keluar.
              </p>
            </div>
          </>
        )}

        {tahap === 'tunai' && (
          <>
            <div className="mh">
              <div><h2>Tunai</h2><div className="sub">{struk}</div></div>
              <button className="x" onClick={() => setTahap('pilih')} aria-label="Kembali">×</button>
            </div>
            <div className="mb">
              <div className="tagihan">
                <p className="lb">Total tagihan</p>
                <p className="vl">{rupiah(total)}</p>
              </div>
              <div className="cepat">
                {nilaiCepat(total).map((v, i) => (
                  <button key={v} className={i === 0 ? 'pas' : ''} onClick={() => setDiterima(v)}>
                    {i === 0 ? 'Uang pas' : rupiah(v)}
                  </button>
                ))}
              </div>
              <div className="pad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button key={n} onClick={() => setDiterima((d) => Math.min(d * 10 + n, 99_999_999))}>
                    {n}
                  </button>
                ))}
                <button onClick={() => setDiterima((d) => Math.min(d * 1000, 99_999_999))}>000</button>
                <button onClick={() => setDiterima((d) => d * 10)}>0</button>
                <button className="hapus" onClick={() => setDiterima((d) => Math.floor(d / 10))}>⌫</button>
              </div>
              <div className="terima">
                <span className="lb">Uang diterima</span>
                <span className="vl">{rupiah(diterima)}</span>
              </div>
              <div className={`kembali ${kembalian < 0 ? 'kurang' : ''}`}>
                <span className="lb">{kembalian < 0 ? 'Kurang' : 'Kembalian'}</span>
                <span className="vl">{rupiah(Math.abs(kembalian))}</span>
              </div>
            </div>
            <div className="mf">
              <button className="tbl utama" disabled={kembalian < 0} onClick={bayarTunai}>
                Selesaikan &amp; cetak struk
              </button>
            </div>
          </>
        )}

        {tahap === 'edc-pilih' && (
          <>
            <div className="mh">
              <div><h2>Mesin EDC</h2><div className="sub">{rupiah(total)}</div></div>
              <button className="x" onClick={() => setTahap('pilih')} aria-label="Kembali">×</button>
            </div>
            <div className="mb">
              {galat && <div className="galat">{galat}</div>}
              {MESIN_EDC.map((m) => (
                <button key={m.bank} className="tender" onClick={() => void bayarEdc(m.bank)}>
                  <div className="ic">💳</div>
                  <div><b>EDC {m.bank}</b><span>{m.alamat}</span></div>
                </button>
              ))}
              <p className="petunjuk">
                Nominal dikirim langsung ke mesin. Kasir tidak mengetik ulang — ini
                menghapus penyebab paling umum tutup buku tidak cocok.
              </p>
            </div>
          </>
        )}

        {tahap === 'edc-proses' && (
          <>
            <div className="mh"><div><h2>EDC {bank}</h2><div className="sub">{rupiah(total)}</div></div></div>
            <div className="mb">
              <div className="proses">
                <div className="putar" />
                <h3>Menunggu kartu</h3>
                <p>Nominal sudah tampil di mesin. Minta pelanggan tap, gesek, atau masukkan kartu.</p>
              </div>
            </div>
          </>
        )}

        {tahap === 'qris' && (
          <Qris total={total} struk={struk}
                siapkanPesanan={siapkanPesananDaring}
                batal={() => setTahap('pilih')}
                lunas={async (info) => {
                  setHasil({ tender: 'qris', ...info });
                  setTahap('selesai');
                  // Pesanan DAN pembayaran sudah tercatat di server: pesanan
                  // dikirim sebelum QR dibuat, dan status lunas datang dari
                  // callback gateway. Kasir tinggal membersihkan keranjang.
                  await selesaiQris();
                }} />
        )}

        {tahap === 'selesai' && (
          <>
            <div className="mb">
              <div className="selesai">
                <div className="centang">✓</div>
                <h3>Pembayaran diterima</h3>
                {hasil?.tender === 'tunai'
                  ? <p className="kb">Kembalian <b>{rupiah(hasil.kembalian)}</b></p>
                  : <p className="kb">{struk}</p>}
                <div className="slip">
                  <div><span>Struk</span><span>{struk}</span></div>
                  <div><span>Total</span><span>{rupiah(total)}</span></div>
                  <hr />
                  {hasil?.tender === 'tunai' && <>
                    <div><span>Tunai diterima</span><span>{rupiah(diterima)}</span></div>
                    <div><span>Kembalian</span><span>{rupiah(hasil.kembalian)}</span></div>
                  </>}
                  {hasil?.tender === 'edc' && <>
                    <div><span>Bank</span><span>{hasil.bank}</span></div>
                    <div><span>Kartu</span><span>•••• {hasil.kartuAkhir}</span></div>
                    <div><span>Jenis</span><span>{hasil.jenisKartu}</span></div>
                    <div><span>Approval</span><span>{hasil.approvalCode}</span></div>
                  </>}
                  {hasil?.tender === 'qris' && <>
                    <div><span>Gateway</span><span>{hasil.namaGateway}</span></div>
                    <div><span>Reference</span><span>{hasil.referenceId}</span></div>
                  </>}
                </div>
              </div>
            </div>
            <div className="mf">
              <button className="tbl utama" onClick={tutup}>Pesanan berikutnya</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- QRIS
/**
 * Alur QRIS.
 *
 * Urutannya tidak bisa ditukar: pesanan harus ada di server sebelum QR bisa
 * dibuat, karena gateway menagih nominal yang mengacu pada pesanan itu.
 *
 * Status lunas TIDAK PERNAH ditetapkan dari sini. Kasir hanya menanyakan
 * status; yang menentukan adalah callback gateway yang tanda tangannya sudah
 * diverifikasi server. Penarikan berkala ini jaring pengaman kalau callback
 * telat, bukan sumber kebenaran.
 */
function Qris({
  total, struk, siapkanPesanan, batal, lunas,
}: {
  total: number; struk: string;
  siapkanPesanan: () => Promise<string>;
  batal: () => void;
  lunas: (info: { namaGateway: string; referenceId: string }) => Promise<void>;
}) {
  const [qr, setQr] = useState<{
    paymentId: string; referenceId: string; namaGateway: string;
    qrString: string; qrImageUrl?: string;
  } | null>(null);
  const [galat, setGalat] = useState<string | null>(null);
  const [sisa, setSisa] = useState(300);
  const kanvas = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let hidup = true;
    let jamPoll: number | undefined;
    let jamHitung: number | undefined;

    (async () => {
      try {
        const orderId = await siapkanPesanan();
        const hasil = await api.buatQris(orderId);
        if (!hidup) return;
        setQr(hasil);

        jamPoll = window.setInterval(async () => {
          try {
            const st = await api.statusQris(hasil.paymentId);
            if (!hidup) return;
            if (st.status === 'lunas') {
              window.clearInterval(jamPoll);
              await lunas({ namaGateway: hasil.namaGateway, referenceId: hasil.referenceId });
            } else if (st.status === 'gagal' || st.status === 'kedaluwarsa') {
              window.clearInterval(jamPoll);
              setGalat('Pembayaran gagal atau QR kedaluwarsa. Buat ulang atau pilih tender lain.');
            }
          } catch {
            // Jaringan tersendat saat menunggu. Diamkan — percobaan berikutnya
            // 5 detik lagi. Menampilkan galat di sini hanya membuat kasir panik.
          }
        }, 5000);
      } catch (e) {
        if (!hidup) return;
        if (e instanceof GalatJaringan) {
          setGalat('Tidak bisa menghubungi server. QRIS butuh internet — pakai tunai atau EDC.');
        } else if (e instanceof GalatApi) {
          setGalat(e.message);
        } else {
          setGalat('Gagal membuat QR.');
        }
      }
    })();

    jamHitung = window.setInterval(() => setSisa((s) => Math.max(0, s - 1)), 1000);
    return () => {
      hidup = false;
      if (jamPoll) window.clearInterval(jamPoll);
      if (jamHitung) window.clearInterval(jamHitung);
    };
  }, []);

  // Render string QRIS jadi gambar. Sebagian gateway (Midtrans) mengirim URL
  // gambar siap pakai; sisanya mengirim string mentah yang harus digambar.
  useEffect(() => {
    if (!qr?.qrString || !kanvas.current) return;
    void QRCode.toCanvas(kanvas.current, qr.qrString, {
      width: 168, margin: 0,
      color: { dark: '#111111', light: '#ffffff' },
    }).catch(() => setGalat('QR diterima tapi gagal digambar. Coba tender lain.'));
  }, [qr?.qrString]);

  return (
    <>
      <div className="mh">
        <div><h2>QRIS</h2><div className="sub">{struk} · {rupiah(total)}</div></div>
        <button className="x" onClick={batal} aria-label="Batal">×</button>
      </div>
      <div className="mb">
        {galat ? (
          <div className="galat">{galat}</div>
        ) : qr ? (
          <>
            <div className="qrkotak">
              {qr.qrImageUrl
                ? <img src={qr.qrImageUrl} alt="Kode QRIS" />
                : <canvas ref={kanvas} width={168} height={168} />}
            </div>
            <div className="qrmeta">
              <div>Reference <b>{qr.referenceId}</b></div>
              <div>Gateway <b>{qr.namaGateway}</b></div>
              <div>
                <span className="hitung">
                  ⏱ {Math.floor(sisa / 60)}:{String(sisa % 60).padStart(2, '0')}
                </span>
              </div>
            </div>
            <p className="petunjuk" style={{ textAlign: 'center' }}>
              Server menunggu callback gateway. Status lunas tidak pernah dipercaya
              dari perangkat kasir — hanya dari callback yang tanda tangannya terverifikasi.
            </p>
          </>
        ) : (
          <div className="proses">
            <div className="putar" />
            <h3>Membuat QR…</h3>
            <p>Mengirim pesanan ke server lalu meminta kode ke gateway.</p>
          </div>
        )}
      </div>
      <div className="mf">
        <button className="tbl samar" onClick={batal}>Kembali</button>
      </div>
    </>
  );
}

// -------------------------------------------------------------- jembatan
const MESIN_EDC = [
  { bank: 'BCA', alamat: '192.168.1.50 · ECR' },
  { bank: 'Mandiri', alamat: '192.168.1.51 · ECR' },
  { bank: 'BRI', alamat: 'serial USB' },
];

interface BalasanEcr {
  ok: boolean;
  pesan: string;
  approvalCode: string;
  kartuAkhir: string;
  jenisKartu: string;
  batch: string;
}

/**
 * Jembatan ke mesin EDC.
 *
 * BELUM TERSAMBUNG ke mesin sungguhan — ini penampung yang meniru balasan
 * ECR supaya alur kasir bisa diuji ujung ke ujung. Penggantinya nanti
 * berbicara TCP ke alamat IP mesin di jaringan lokal (atau serial lewat USB
 * pada Android), dan bentuk balasannya sudah dipatok di sini supaya
 * penggantian tidak mengubah kode pemanggil.
 *
 * Sertifikasi ECR per bank adalah proses panjang di luar kendali kami —
 * lihat cetak biru bagian risiko.
 */
async function jembatanEcr(bank: string, jumlah: number): Promise<BalasanEcr> {
  await new Promise((r) => setTimeout(r, 2200));
  return {
    ok: true,
    pesan: 'Disetujui',
    approvalCode: String(Math.floor(Math.random() * 900000) + 100000),
    kartuAkhir: String(Math.floor(Math.random() * 9000) + 1000),
    jenisKartu: ['Debit BCA', 'Visa Credit', 'Mastercard Debit', 'GPN Debit'][
      Math.floor(Math.random() * 4)
    ]!,
    batch: String(Math.floor(Math.random() * 900) + 100),
  };
}

function nilaiCepat(total: number): number[] {
  const set = new Set<number>([
    total,
    Math.ceil(total / 50_000) * 50_000,
    Math.ceil(total / 100_000) * 100_000,
  ]);
  return [...set].slice(0, 3);
}
